import { useQuery } from "@tanstack/react-query";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { api, ApiError } from "./api";
import { isNative } from "./native";
import type { MeDTO, PasskeyDTO } from "@shared/types";

export const passkeysSupported = () => typeof window !== "undefined" && !isNative() && browserSupportsWebAuthn();

/** The person closed the Face ID / fingerprint prompt: not an error worth showing. */
export const cancelled = (err: unknown) => err instanceof Error && (err.name === "NotAllowedError" || err.name === "AbortError");

export function usePasskeys(enabled = true) {
  return useQuery<{ passkeys: PasskeyDTO[] }, ApiError>({
    queryKey: ["passkeys"],
    queryFn: () => api.get("/security/passkeys"),
    enabled,
  });
}

/** Step 1 of adding a passkey: the parent's password gets options for this device. */
export const passkeyOptions = (password: string) => api.post<PublicKeyCredentialCreationOptionsJSON>("/security/passkeys/options", { password });

/** Step 2, straight from a tap (Safari needs one): create the passkey and save it. */
export async function createPasskey(optionsJSON: PublicKeyCredentialCreationOptionsJSON): Promise<{ passkeys: PasskeyDTO[] }> {
  const response = await startRegistration({ optionsJSON });
  return api.post("/security/passkeys", { response });
}

/** A kid adding a passkey from their own view: their PIN (if they have one) gets options for this device. */
export const kidPasskeyOptions = (pin?: string) => api.post<PublicKeyCredentialCreationOptionsJSON>("/auth/kid-passkey/options", { pin });

/** Then, straight from a tap: create the kid's passkey and save it. */
export async function createKidPasskey(optionsJSON: PublicKeyCredentialCreationOptionsJSON): Promise<void> {
  const response = await startRegistration({ optionsJSON });
  await api.post("/auth/kid-passkey", { response });
}

/** For the login screen: options fetched ahead of the tap. */
export const signInOptions = () => api.post<PublicKeyCredentialRequestOptionsJSON>("/auth/passkey/options");

/** Call straight from a tap: whoever the passkey belongs to is signed in. */
export async function signInWithPasskey(optionsJSON: PublicKeyCredentialRequestOptionsJSON): Promise<MeDTO> {
  const response = await startAuthentication({ optionsJSON });
  return api.post<MeDTO>("/auth/passkey", { response });
}

/** From a kid's view: options to switch back with a parent's passkey, or null if no parent has one. */
export async function switchBackOptions(): Promise<PublicKeyCredentialRequestOptionsJSON | null> {
  return api.post<PublicKeyCredentialRequestOptionsJSON>("/auth/switch/parent/passkey/options").catch((err) => {
    if (err instanceof ApiError && err.code === "no_passkeys") return null;
    throw err;
  });
}

/** Call straight from a tap: Safari only shows the passkey prompt in response to one. */
export async function switchBackWithPasskey(optionsJSON: PublicKeyCredentialRequestOptionsJSON): Promise<MeDTO> {
  const response = await startAuthentication({ optionsJSON });
  return api.post<MeDTO>("/auth/switch/parent/passkey", { response });
}
